import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileArchive, Mail, Send, ShieldCheck, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Section, SectionTitle } from "@/components/quote/Section";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/quoteText";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

async function downloadFile(path, filename) {
  const { data, headers } = await axios.get(`${API}/backup/${path}`, { responseType: "blob" });
  const name = (headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || filename;
  const url = URL.createObjectURL(data);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  a.click();
  URL.revokeObjectURL(url);
}

function Downloads() {
  const items = [
    { path: "customers.csv", label: "Customers", zh: "客户", desc: "Names, phones, vehicles, notes" },
    { path: "quotes.csv", label: "Quotes", zh: "报价", desc: "Every quote with status, totals & items" },
    { path: "settings.csv", label: "Settings", zh: "设置", desc: "Prices, website content, reviews" },
  ];
  const dl = async (path) => { try { await downloadFile(path); toast.success("Download started"); } catch { toast.error("Download failed"); } };
  return (
    <Section>
      <SectionTitle n="1" en="Download CSV backup" zh="下载备份" icon={Download} />
      <p className="text-xs text-slate-500 mt-1">Open in Excel / Google Sheets. Save the files to Google Drive, Dropbox or iCloud for a safe copy.</p>
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        {items.map((it) => (
          <button key={it.path} onClick={() => dl(it.path)} data-testid={`backup-download-${it.path.split(".")[0]}`}
            className="rounded-xl border-2 border-slate-700 bg-slate-900/50 p-4 text-left hover:border-amber-400/60 transition-colors">
            <div className="font-semibold">{it.label} <span className="font-zh text-xs text-amber-300/70">{it.zh}</span></div>
            <div className="text-xs text-slate-500 mt-0.5">{it.desc}</div>
            <div className="text-xs text-amber-300 mt-2 flex items-center gap-1"><Download className="w-3.5 h-3.5" /> .csv</div>
          </button>
        ))}
      </div>
      <Button onClick={() => dl("all.zip")} data-testid="backup-download-all" className="mt-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"><FileArchive className="w-4 h-4 mr-2" /> Download all (ZIP)</Button>
    </Section>
  );
}

function EmailSchedule({ status, onChanged }) {
  const [cfg, setCfg] = useState(null);
  const [emails, setEmails] = useState("");
  const [key, setKey] = useState("");
  const [sender, setSender] = useState("");
  const [sending, setSending] = useState(false);
  const load = () => axios.get(`${API}/backup/config`).then(({ data }) => { setCfg(data); setEmails(data.emails.join(", ")); setSender(data.sender || ""); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!cfg) return null;

  const toggleDay = (d) => setCfg((c) => ({ ...c, days: c.days.includes(d) ? c.days.filter((x) => x !== d) : [...c.days, d].sort((a, b) => a - b) }));
  const save = async () => {
    const { data } = await axios.put(`${API}/backup/config`, { emails: emails.split(/[,;\n]/), days: cfg.days, hour: cfg.hour, resend_api_key: key || null, sender: sender || null });
    setCfg(data); setKey("");
    toast.success("Backup schedule saved");
    onChanged();
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/backup/send-now`);
      toast.success("Backup email sent", { description: `To ${data.to.join(", ")}` });
      load(); onChanged();
    } catch (e) {
      toast.error("Could not send", { description: e?.response?.data?.detail || "Check the API key and recipients" });
    } finally { setSending(false); }
  };
  const last = status?.last_result;

  return (
    <Section>
      <SectionTitle n="2" en="Automatic email backup" zh="自动邮件备份" icon={Mail} />
      <p className="text-xs text-slate-500 mt-1">On each selected day (Singapore time) the three CSV files are emailed automatically. Sent via Resend.</p>
      <div className="grid md:grid-cols-2 gap-5 mt-4">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Send to <span className="font-zh normal-case tracking-normal text-amber-300/70">收件邮箱</span></Label>
            <Input value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="backup-emails-input" placeholder="owner@example.com, sales@example.com" className="bg-slate-950/60 border-slate-700" />
            <p className="text-[11px] text-slate-500">Separate several addresses with commas.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Resend API key {cfg.key_set && <span className="text-emerald-300 normal-case tracking-normal ml-1">· saved {cfg.key_hint}</span>}</Label>
            <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} data-testid="backup-api-key-input" placeholder={cfg.key_set ? "Paste a new key to replace" : "re_…"} className="bg-slate-950/60 border-slate-700 font-mono-num" />
            <p className="text-[11px] text-slate-500">Get one free at <span className="text-sky-300">resend.com → API Keys</span>. Until you verify your own domain, Resend's test sender only delivers to the account owner's email.</p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Days of the month <span className="font-zh normal-case tracking-normal text-amber-300/70">每月日期</span></Label>
            <div className="grid grid-cols-8 sm:grid-cols-11 gap-1" data-testid="backup-days-grid">
              {DAYS.map((d) => (
                <button key={d} data-on={cfg.days.includes(d)} onClick={() => toggleDay(d)} data-testid={`backup-day-${d}`}
                  className="h-8 rounded-md text-xs font-mono-num font-semibold border border-slate-700 text-slate-400 transition-colors data-[on=true]:bg-amber-400 data-[on=true]:border-amber-400 data-[on=true]:text-slate-950">{d}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Sender <span className="font-zh normal-case tracking-normal text-amber-300/70">发件人</span></Label>
            <Input value={sender} onChange={(e) => setSender(e.target.value)} data-testid="backup-sender-input" placeholder="Hup Ley Huat Backup <backup@hupleyhuat.com>" className="bg-slate-950/60 border-slate-700" />
            <p className="text-[11px] text-slate-500">After verifying hupleyhuat.com in Resend, use an address on that domain so all recipients receive it.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Send after (hour, SGT)</Label>
            <Input type="number" min="0" max="23" value={cfg.hour} onChange={(e) => setCfg({ ...cfg, hour: Number(e.target.value) })} data-testid="backup-hour-input" className="bg-slate-950/60 border-slate-700 w-24 font-mono-num" />
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-5">
        <Button onClick={save} data-testid="backup-save-config-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">Save schedule</Button>
        <Button onClick={sendNow} disabled={sending} variant="outline" data-testid="backup-send-now-btn" className="border-emerald-500/50 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"><Send className="w-4 h-4 mr-2" /> {sending ? "Sending…" : "Send backup email now"}</Button>
        {status?.last_sent_at && <span className="text-xs text-slate-400" data-testid="backup-last-sent">Last sent {fmtDateTime(status.last_sent_at)}</span>}
        {last && !last.ok && <span className="text-xs text-rose-300" data-testid="backup-last-error">Last attempt failed: {last.error}</span>}
      </div>
    </Section>
  );
}

export default function Backup() {
  const { isOwner } = useAuth();
  const [status, setStatus] = useState(null);
  const loadStatus = () => axios.get(`${API}/backup/status`).then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { loadStatus(); }, []);
  return (
    <div className="min-h-screen workshop-bg text-slate-100">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold">Data safety · 数据安全</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-1">Backup &amp; Export <span className="font-zh text-amber-300/70 text-2xl">备份导出</span></h2>
          {status && (
            <p className="text-sm text-slate-400 mt-1 flex items-center gap-2" data-testid="backup-schedule-summary">
              <CalendarClock className="w-4 h-4 text-amber-400" /> Backup days: {status.days.length ? status.days.map((d) => `${d}${["st", "nd", "rd"][((d + 90) % 100 - 10) % 10 - 1] || "th"}`).join(" & ") : "none"} of each month
              {status.email_configured ? <span className="text-emerald-300 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> email on</span> : <span className="text-amber-300">· email not set up yet</span>}
            </p>
          )}
        </div>
        <Downloads />
        {isOwner && <EmailSchedule status={status} onChanged={loadStatus} />}
      </div>
    </div>
  );
}
